/**
 * ClinVea Copilot client (medopd-docs 154, platform page 02-features/14-ai-features/11-copilot.md).
 *
 * The answer streams as Server-Sent Events from a POST, so it uses fetch + ReadableStream rather than
 * EventSource, with the same headers as every other staff call (session cookie, CSRF on the write, active clinic).
 * Everything else is plain JSON through fetchApi.
 */
import {
  API_BASE_URL, fetchApi, refreshStaffSession, staffRequestHeaders, type ApiResponse,
} from '../../../lib/api/client';
import { createSseParser } from './sseParser';

// ─── Shapes ──────────────────────────────────────────────────────────────────────────────

export interface CopilotToolEvent {
  name: string;
  /** English activity line from the server, already showing real names ("Looked up appointments for tomorrow"). */
  label?: string;
  /** A translation key for the line (`copilot.tool.<name>`), so it can be shown in te/hi. */
  labelKey?: string;
  /**
   * The whole-number inputs the model gave the tool (`{from_offset_days: 1}`); never names or free text. Only the
   * values the model set are sent, so a missing one means the tool's default.
   */
  labelParams?: Record<string, unknown>;
  ok?: boolean;
}

export interface CopilotSource {
  title: string;
  origin?: string;
}

/** A stored proposal's state (GET /conversations/{id}); live cards from the stream carry none (pending). */
export type CopilotProposalStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED';

export interface CopilotProposal {
  id: string;
  /** book_appointment, cancel_appointment, reschedule_appointment, create_follow_up, send_reminder, stock_reorder_draft */
  action: string;
  conversationId?: string;
  title?: string;
  details?: Record<string, unknown>;
  /** ISO instant after which the server refuses the card (410 PROPOSAL_EXPIRED). */
  expiresAt?: string;
  status?: CopilotProposalStatus;
}

export interface CopilotDone {
  conversationId?: string;
  messageId?: string;
  questionId?: string;
  usage?: Record<string, unknown>;
}

export interface CopilotErrorEvent {
  code: string;
  message?: string;
}

export interface CopilotChatBody {
  conversationId?: string | null;
  message: string;
  patientId?: string | null;
  locale?: string;
}

export interface CopilotStreamHandlers {
  onTool?: (e: CopilotToolEvent) => void;
  onDelta?: (text: string) => void;
  onSources?: (items: CopilotSource[]) => void;
  onProposal?: (p: CopilotProposal) => void;
  onDone?: (d: CopilotDone) => void;
  onError?: (e: CopilotErrorEvent) => void;
}

export interface CopilotConversationSummary {
  id: string;
  title?: string | null;
  namesAvailable?: boolean;
  messageCount?: number;
  createdAt?: string;
  lastMessageAt?: string;
}

export interface CopilotStoredMessage {
  id: string;
  role: 'user' | 'assistant' | string;
  content: string;
  blocks?: {
    tools?: CopilotToolEvent[];
    sources?: CopilotSource[];
    /** Stored cards carry only id, action and the server's status (no title, details or expiresAt). */
    proposals?: Array<{ id: string; action: string; status?: string; title?: string; details?: Record<string, unknown>; expiresAt?: string }>;
    unverifiedNumbers?: string[];
  } | null;
  feedback?: string | null;
  createdAt?: string;
}

export interface CopilotConversation {
  id: string;
  title?: string | null;
  namesAvailable?: boolean;
  messages: CopilotStoredMessage[];
}

export interface CopilotSuggestion {
  key?: string;
  text?: string;
  params?: Record<string, unknown>;
  category?: string;
}

export type FeedbackRating = 'up' | 'down';
export type FeedbackReason = 'WRONG' | 'NOT_HELPFUL' | 'UNSAFE' | 'TOO_SLOW' | 'OTHER';
export const FEEDBACK_REASONS: FeedbackReason[] = ['WRONG', 'NOT_HELPFUL', 'UNSAFE', 'TOO_SLOW', 'OTHER'];

export interface ProposalOutcome {
  ok: boolean;
  /** The endpoint does not exist on this server yet (Phase 3 not deployed). */
  unavailable?: boolean;
  /** Error code on failure, for the friendly message. */
  code?: string;
  /** On success: CONFIRMED or CANCELLED. */
  status?: string;
  /** The server's own words: the result line on success (`message`, else `summary`), the reason on failure. */
  message?: string;
  /** On success: the i18n key of the result line (`copilot.result.*`), shown in the user's language when known. */
  messageKey?: string;
  /** Flat values for `messageKey` (patient, doctor, token, at, date, when, count). */
  messageParams?: Record<string, unknown>;
}

// ─── Errors ──────────────────────────────────────────────────────────────────────────────

/** Codes the UI knows how to explain. Anything else is shown as the generic message. */
export const COPILOT_ERROR_CODES = [
  'BAD_REQUEST', 'MODULE_DISABLED', 'FEATURE_DISABLED', 'NOT_ALLOWED', 'NOT_FOUND', 'CONVERSATION_BUSY',
  'PRIVACY_REFUSED', 'QUOTA_EXCEEDED', 'TOKEN_QUOTA_EXCEEDED', 'RATE_LIMITED', 'INTERNAL', 'AI_OFF',
  'AI_UNAVAILABLE', 'BUSY', 'NETWORK', 'INTERRUPTED', 'SIGNED_OUT', 'CANCELLED',
] as const;

/** The code a failed HTTP call stands for when the body did not carry one. */
export function codeForStatus(status: number): string {
  switch (status) {
    case 400: return 'BAD_REQUEST';
    case 401: return 'SIGNED_OUT';
    case 403: return 'NOT_ALLOWED';
    case 404: return 'NOT_FOUND';
    case 409: return 'CONVERSATION_BUSY';
    case 410: return 'PROPOSAL_EXPIRED';
    case 422: return 'PRIVACY_REFUSED';
    case 429: return 'RATE_LIMITED';
    case 503: return 'AI_UNAVAILABLE';
    default: return 'INTERNAL';
  }
}

const CODE_PATTERN = /^[A-Z][A-Z_]+$/;

function parseJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return null; }
}

function asObject(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/**
 * Reads `{ error: CODE, message }` from a failed Copilot response. A 404/405 that does not carry a Copilot code
 * comes from the global handler (`errorCode: RESOURCE_NOT_FOUND / METHOD_NOT_ALLOWED`): the route itself is
 * missing on this server (an older build, or Phase 3 not deployed yet).
 */
async function errorFrom(res: Response): Promise<CopilotErrorEvent & { routeMissing: boolean }> {
  const json = asObject(parseJson(await res.text().catch(() => '')));
  const raw = typeof json.error === 'string' ? json.error : typeof json.code === 'string' ? json.code : '';
  const known = CODE_PATTERN.test(raw);
  const routeMissing = (res.status === 404 || res.status === 405) && !known;
  const code = known ? raw : routeMissing ? 'AI_UNAVAILABLE' : codeForStatus(res.status);
  return { code, message: known && typeof json.message === 'string' ? json.message : undefined, routeMissing };
}

// ─── Streaming chat ──────────────────────────────────────────────────────────────────────

/** A tool line from the stream, the sync reply or a stored conversation: `{name, label, labelKey, labelParams, ok}`. */
export function toToolEvent(raw: unknown): CopilotToolEvent | null {
  const p = asObject(raw);
  if (typeof p.name !== 'string') return null;
  const params = p.labelParams && typeof p.labelParams === 'object' && !Array.isArray(p.labelParams)
    ? (p.labelParams as Record<string, unknown>) : undefined;
  return {
    name: p.name,
    label: typeof p.label === 'string' ? p.label : undefined,
    labelKey: typeof p.labelKey === 'string' ? p.labelKey : undefined,
    labelParams: params,
    ...(typeof p.ok === 'boolean' ? { ok: p.ok } : {}),
  };
}

const PROPOSAL_STATUSES = new Set(['PENDING', 'CONFIRMED', 'CANCELLED', 'EXPIRED']);

/** A proposal card from the stream (`{id, conversationId, action, title, details, expiresAt}`) or a stored one (`{id, action, status}`). */
export function toProposal(raw: unknown): CopilotProposal | null {
  const p = asObject(raw);
  if (typeof p.id !== 'string' || !p.id) return null;
  const status = typeof p.status === 'string' ? p.status.toUpperCase() : '';
  return {
    id: p.id,
    action: typeof p.action === 'string' ? p.action : '',
    conversationId: typeof p.conversationId === 'string' ? p.conversationId : undefined,
    title: typeof p.title === 'string' ? p.title : undefined,
    details: p.details && typeof p.details === 'object' && !Array.isArray(p.details) ? (p.details as Record<string, unknown>) : undefined,
    expiresAt: typeof p.expiresAt === 'string' ? p.expiresAt : undefined,
    status: PROPOSAL_STATUSES.has(status) ? (status as CopilotProposalStatus) : undefined,
  };
}

function dispatchEvent(event: string, data: string, h: CopilotStreamHandlers): 'done' | 'error' | null {
  const payload = asObject(parseJson(data));
  switch (event) {
    case 'tool': {
      const tool = toToolEvent(payload);
      if (tool) h.onTool?.(tool);
      return null;
    }
    case 'delta':
      if (typeof payload.text === 'string') h.onDelta?.(payload.text);
      return null;
    case 'sources':
      if (Array.isArray(payload.items)) h.onSources?.(payload.items as CopilotSource[]);
      return null;
    case 'proposal': {
      const proposal = toProposal(payload);
      if (proposal) h.onProposal?.(proposal);
      return null;
    }
    case 'done':
      h.onDone?.(payload as CopilotDone);
      return 'done';
    case 'error':
      h.onError?.({
        code: typeof payload.code === 'string' ? payload.code : 'INTERNAL',
        message: typeof payload.message === 'string' ? payload.message : undefined,
      });
      return 'error';
    default:
      return null;
  }
}

async function postChat(
  body: CopilotChatBody, signal: AbortSignal | undefined, path: string, accept: string, retried = false,
  sendBody: unknown = body,
): Promise<Response> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: staffRequestHeaders('POST', undefined, { Accept: accept }),
    body: JSON.stringify(sendBody),
    credentials: 'include',
    signal,
  });
  // The access token may have expired a moment ago: one shared silent refresh, then one retry (as fetchApi does).
  if (res.status === 401 && !retried && await refreshStaffSession()) {
    return postChat(body, signal, path, accept, true, sendBody);
  }
  return res;
}

/** The JSON variant, for browsers without readable response streams. Replays the result as events. */
export async function chatSync(body: CopilotChatBody, h: CopilotStreamHandlers, signal?: AbortSignal): Promise<void> {
  const res = await postChat(body, signal, '/api/ai/copilot/chat/sync', 'application/json');
  if (!res.ok) {
    { const e = await errorFrom(res); h.onError?.({ code: e.code, message: e.message }); }
    return;
  }
  const json = asObject(parseJson(await res.text()));
  const data = asObject('data' in json ? json.data : json);
  for (const raw of Array.isArray(data.tools) ? data.tools : []) {
    const tool = toToolEvent(raw);
    if (tool) h.onTool?.(tool);
  }
  if (typeof data.answer === 'string' && data.answer) h.onDelta?.(data.answer);
  if (Array.isArray(data.sources) && data.sources.length) h.onSources?.(data.sources as CopilotSource[]);
  for (const raw of Array.isArray(data.proposals) ? data.proposals : []) {
    const p = toProposal(raw);
    if (p) h.onProposal?.(p);
  }
  h.onDone?.({
    conversationId: data.conversationId as string | undefined,
    messageId: data.messageId as string | undefined,
    questionId: data.questionId as string | undefined,
    usage: data.usage as Record<string, unknown> | undefined,
  });
}

/**
 * Asks one question and calls the handlers as the answer streams in. Resolves when the stream ends; an abort
 * (the Stop button) resolves quietly. Exactly one of onDone / onError is called unless aborted.
 */
export async function streamCopilotChat(body: CopilotChatBody, h: CopilotStreamHandlers, signal?: AbortSignal): Promise<void> {
  try {
    const res = await postChat(body, signal, '/api/ai/copilot/chat', 'text/event-stream');
    if (!res.ok) {
      { const e = await errorFrom(res); h.onError?.({ code: e.code, message: e.message }); }
      return;
    }
    const reader = res.body && typeof res.body.getReader === 'function' ? res.body.getReader() : null;
    if (!reader) {
      await chatSync(body, h, signal);
      return;
    }
    let terminal: 'done' | 'error' | null = null;
    const parser = createSseParser(({ event, data }) => {
      if (terminal) return;
      terminal = dispatchEvent(event, data, h);
    });
    const decoder = new TextDecoder();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.feed(typeof value === 'string' ? value : decoder.decode(value, { stream: true }));
    }
    parser.feed(decoder.decode());
    parser.end();
    if (!terminal && !signal?.aborted) h.onError?.({ code: 'INTERRUPTED' });
  } catch (e) {
    if (signal?.aborted || (e instanceof DOMException && e.name === 'AbortError')) return;
    h.onError?.({ code: 'NETWORK' });
  }
}

// ─── Conversations, feedback, proposals, suggestions ─────────────────────────────────────

function unwrapList<T>(res: ApiResponse<unknown>, keys: string[]): T[] {
  const d = res.data;
  if (Array.isArray(d)) return d as T[];
  const o = asObject(d);
  for (const k of keys) if (Array.isArray(o[k])) return o[k] as T[];
  return [];
}

export class CopilotRequestError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = 'CopilotRequestError';
  }
}

function orFail<T>(res: ApiResponse<T>): T {
  if (res.error) throw new CopilotRequestError(res.error, res.status);
  return res.data as T;
}

/** Normalises whatever shape /suggestions returns: keys, objects with a key or text, or a wrapper object. */
export function normaliseSuggestions(raw: unknown): CopilotSuggestion[] {
  let list: unknown[] = [];
  if (Array.isArray(raw)) list = raw;
  else {
    const o = asObject(raw);
    for (const k of ['items', 'suggestions', 'questions', 'keys']) {
      if (Array.isArray(o[k])) { list = o[k] as unknown[]; break; }
    }
  }
  const out: CopilotSuggestion[] = [];
  for (const item of list) {
    if (typeof item === 'string' && item.trim()) out.push({ key: item.trim() });
    else if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      const key = [o.key, o.i18nKey, o.questionKey, o.labelKey, o.id].find((v) => typeof v === 'string') as string | undefined;
      const text = [o.text, o.question, o.label].find((v) => typeof v === 'string') as string | undefined;
      if (key || text) {
        out.push({
          key, text, params: o.params && typeof o.params === 'object' ? o.params as Record<string, unknown> : undefined,
          ...(typeof o.category === 'string' ? { category: o.category } : {}),
        });
      }
    }
  }
  return out;
}

export const copilotApi = {
  stream: streamCopilotChat,

  listConversations: async (limit = 30): Promise<CopilotConversationSummary[]> => {
    const res = await fetchApi<unknown>(`/api/ai/copilot/conversations?limit=${limit}`);
    if (res.error) throw new CopilotRequestError(res.error, res.status);
    return unwrapList<CopilotConversationSummary>(res, ['items', 'conversations']);
  },

  getConversation: async (id: string): Promise<CopilotConversation> =>
    orFail(await fetchApi<CopilotConversation>(`/api/ai/copilot/conversations/${encodeURIComponent(id)}`)),

  deleteConversation: async (id: string): Promise<void> => {
    orFail(await fetchApi<void>(`/api/ai/copilot/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }));
  },

  feedback: async (messageId: string, rating: FeedbackRating, reason?: FeedbackReason): Promise<void> => {
    orFail(await fetchApi<void>('/api/ai/copilot/feedback', {
      method: 'POST',
      body: JSON.stringify({ messageId, rating, ...(reason ? { reason } : {}) }),
    }));
  },

  /** Phase 3. A 404/405 means this server does not offer it yet: `unavailable`, and the card says so. */
  decideProposal: async (id: string, decision: 'confirm' | 'cancel', conversationId?: string | null): Promise<ProposalOutcome> => {
    const body: CopilotChatBody = { conversationId, message: '' };
    let res: Response;
    try {
      res = await postChat(body, undefined, `/api/ai/copilot/proposals/${encodeURIComponent(id)}/${decision}`,
        'application/json', false, conversationId ? { conversationId } : {});
    } catch {
      return { ok: false, code: 'NETWORK' };
    }
    if (!res.ok) {
      const err = await errorFrom(res);
      if (err.routeMissing) return { ok: false, unavailable: true };
      return { ok: false, code: err.code, message: err.message };
    }
    const json = asObject(parseJson(await res.text().catch(() => '')));
    const d = asObject('data' in json ? json.data : json);
    const message = [d.message, d.summary].find((v) => typeof v === 'string' && v.trim()) as string | undefined;
    const status = typeof d.status === 'string' ? d.status : decision === 'confirm' ? 'CONFIRMED' : 'CANCELLED';
    const messageKey = typeof d.messageKey === 'string' && d.messageKey.trim() ? d.messageKey : undefined;
    const messageParams = d.messageParams && typeof d.messageParams === 'object' && !Array.isArray(d.messageParams)
      ? d.messageParams as Record<string, unknown> : undefined;
    return { ok: true, status, message, ...(messageKey ? { messageKey } : {}), ...(messageParams ? { messageParams } : {}) };
  },

  /**
   * Role-aware starter questions as keys (`{suggestions:[{key, i18nKey, category}]}`). `patientOpen` adds the
   * patient questions for a doctor. `null` when the server does not offer them (the panel uses its own).
   */
  suggestions: async (patientOpen: boolean): Promise<CopilotSuggestion[] | null> => {
    const res = await fetchApi<unknown>(`/api/ai/copilot/suggestions?patientOpen=${patientOpen ? 'true' : 'false'}`);
    if (res.error) return null;
    const list = normaliseSuggestions(res.data);
    return list.length ? list : null;
  },
};
